import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  Box,
  Button,
  Chip,
  Table,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Typography,
  TableContainer,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import Title from "../../../common/Title";
import ApiLogHistory from "../../../common/ApiLogHistory";

import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import {
  __getPortinLog,
  __updatePortinTmobilej,
  __cancelPortinTmobilej,
} from "../../../../api/sim";
import { getChipColor } from "../../../../constants/value";

const TERMINAL_STATUSES = ["cancelled", "ported", "already_ported"];

const emptyUpdateForm = {
  current_carrier_name: "",
  current_account_number: "",
  current_account_password: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  zipcode: "",
  due_date: "",
  name: "",
  iccid: "",
};

const TmobilejPortinHistory = (props) => {
  const { setAlertMessage } = useAlert();
  const [portinHistory, setPortinHistory] = useState([]);
  const { portin_id } = useParams();

  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [updateForm, setUpdateForm] = useState(emptyUpdateForm);

  const [response, call, loading] = useAxios({
    ...__getPortinLog(portin_id),
  });

  const [updateResp, updateApi, updateLoading] = useAxios();
  const [cancelResp, cancelApi, cancelLoading] = useAxios();

  useEffect(() => {
    if (response && response.status) {
      setPortinHistory(response.data);
    }
  }, [response]);

  useEffect(() => {
    call();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!updateResp?.status) return;
    setAlertMessage({ type: "success", message: "Portin updated" });
    setIsUpdateDialogOpen(false);
    setUpdateForm(emptyUpdateForm);
    call();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResp]);

  useEffect(() => {
    if (!cancelResp?.status) return;
    setAlertMessage({ type: "success", message: "Portin cancelled" });
    call();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelResp]);

  const orderId = portinHistory?.data?.order_id;
  const status = portinHistory?.data?.status;
  const actionsDisabled = !orderId || TERMINAL_STATUSES.includes(status);

  const buildUpdateFormFromPortin = (data) => {
    const extraData = data?.extra_data || {};
    return {
      current_carrier_name: extraData.current_carrier_name || "",
      current_account_number: data?.account_number || "",
      current_account_password: data?.account_password || "",
      street: data?.street || "",
      street2: extraData.street2 || "",
      city: data?.city || "",
      state: data?.state || "",
      zipcode: data?.zipcode || "",
      due_date: "",
      name: data?.name && data.name !== "-" ? data.name : "",
      iccid: data?.iccid && data.iccid !== data?.msisdn ? data.iccid : "",
    };
  };

  const handleOpenUpdateDialog = () => {
    setUpdateForm(buildUpdateFormFromPortin(portinHistory.data));
    setIsUpdateDialogOpen(true);
  };

  const handleCloseUpdateDialog = () => {
    setIsUpdateDialogOpen(false);
    setUpdateForm(emptyUpdateForm);
  };

  const handleUpdateFieldChange = (field) => (e) => {
    setUpdateForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleUpdateSubmit = async () => {
    const payload = Object.fromEntries(
      Object.entries(updateForm).filter(([, value]) => value.trim() !== "")
    );
    await updateApi({
      ...__updatePortinTmobilej(portin_id),
      data: payload,
    });
  };

  const handleCancel = async () => {
    if (
      !window.confirm("Are you sure you want to cancel this port-in request?")
    )
      return;
    await cancelApi(__cancelPortinTmobilej(portin_id));
  };

  return loading ? (
    <Title content="Please wait..." variant="subtitle2" gutterBottom />
  ) : (
    <>
      <Box
        mb={2}
        pt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5" align="left">
          Portin Log
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            disabled={actionsDisabled}
            onClick={handleOpenUpdateDialog}
          >
            Update Port-In
          </Button>
          <LoadingButton
            variant="outlined"
            color="error"
            disabled={actionsDisabled}
            loading={cancelLoading}
            onClick={handleCancel}
          >
            Cancel Port-In
          </LoadingButton>
        </Box>
      </Box>

      {response && portinHistory.data ? (
        <>
          <Box
            sx={{ width: "100%", mb: 5 }}
            display="flex"
            alignItems="center"
            flexDirection="column"
          >
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">ICCID</TableCell>
                    <TableCell align="left">Batch ID</TableCell>
                    <TableCell align="left">Status</TableCell>
                    <TableCell align="left">Plan</TableCell>
                    <TableCell align="left">Zipcode</TableCell>
                    <TableCell align="left">MSISDN</TableCell>
                    <TableCell align="left">Order ID</TableCell>
                    <TableCell align="left">Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell align="left">
                      {portinHistory.data.iccid}
                    </TableCell>
                    <TableCell align="left">
                      <Link
                        underline="none"
                        to={`/tmobilej/portin/batch/${portinHistory.data.batch_id}`}
                      >
                        {portinHistory.data.batch_id}
                      </Link>
                    </TableCell>
                    <TableCell align="left">
                      <Chip
                        label={portinHistory.data.status}
                        size="small"
                        color={getChipColor(portinHistory.data.status)}
                      />
                    </TableCell>
                    <TableCell align="left">
                      {portinHistory.data.plan}
                    </TableCell>
                    <TableCell align="left">
                      {portinHistory.data.zipcode}
                    </TableCell>
                    <TableCell align="left">
                      {portinHistory.data.msisdn}
                    </TableCell>
                    <TableCell align="left">
                      {portinHistory.data.order_id || "-"}
                    </TableCell>
                    <TableCell align="left">
                      {portinHistory.data.last_updated &&
                        `${format(
                          new Date(portinHistory.data.last_updated),
                          "MMMM d, y hh:mm a"
                        )}`}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <ApiLogHistory data={portinHistory.history.data} />
        </>
      ) : (
        <Box mb={2} pt={2}>
          <Typography variant="h5" align="left">
            No Data
          </Typography>
        </Box>
      )}

      <Dialog
        open={isUpdateDialogOpen}
        onClose={handleCloseUpdateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Update Port-In</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Current Carrier Name"
              value={updateForm.current_carrier_name}
              onChange={handleUpdateFieldChange("current_carrier_name")}
              fullWidth
            />
            <TextField
              label="Current Account Number"
              value={updateForm.current_account_number}
              onChange={handleUpdateFieldChange("current_account_number")}
              fullWidth
            />
            <TextField
              label="Current Account Password"
              value={updateForm.current_account_password}
              onChange={handleUpdateFieldChange("current_account_password")}
              fullWidth
            />
            <TextField
              label="Street"
              value={updateForm.street}
              onChange={handleUpdateFieldChange("street")}
              fullWidth
            />
            <TextField
              label="Street 2"
              value={updateForm.street2}
              onChange={handleUpdateFieldChange("street2")}
              fullWidth
            />
            <TextField
              label="City"
              value={updateForm.city}
              onChange={handleUpdateFieldChange("city")}
              fullWidth
            />
            <TextField
              label="State"
              value={updateForm.state}
              onChange={handleUpdateFieldChange("state")}
              fullWidth
            />
            <TextField
              label="Zipcode"
              value={updateForm.zipcode}
              onChange={handleUpdateFieldChange("zipcode")}
              fullWidth
            />
            <TextField
              label="Due Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={updateForm.due_date}
              onChange={handleUpdateFieldChange("due_date")}
              fullWidth
            />
            <TextField
              label="Subscriber Name"
              value={updateForm.name}
              onChange={handleUpdateFieldChange("name")}
              fullWidth
            />
            <TextField
              label="ICCID"
              value={updateForm.iccid}
              onChange={handleUpdateFieldChange("iccid")}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>Cancel</Button>
          <LoadingButton
            variant="contained"
            loading={updateLoading}
            onClick={handleUpdateSubmit}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TmobilejPortinHistory;
